import * as React from 'react';
import { ChevronDown, Settings, LogOut } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from '../avatar';

/**
 * UserMenu component with exact Hyperkit styling
 *
 * Hyperkit Specs:
 * - Avatar: 36px rounded-lg
 * - Dropdown: 240px min width, 12px radius
 * - Animation: 150ms fade + slide
 */

export const UserMenu = React.forwardRef(
  (
    {
      className,
      user,
      onNavigate,
      onLogout,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const menuRef = React.useRef(null);
    const buttonRef = React.useRef(null);

    // Get user display info
    const userName = user?.name || user?.email || 'User';
    const userInitials = userName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    const userPicture = user?.picture;
    const userRole = user?.['https://getclearance.dev/role'] || user?.role || 'User';
    const userEmail = user?.email;

    // Close on click outside
    React.useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)
        ) {
          setOpen(false);
        }
      };

      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    // Close on Escape
    React.useEffect(() => {
      const handleEscape = (event) => {
        if (event.key === 'Escape') {
          setOpen(false);
        }
      };

      if (open) {
        document.addEventListener('keydown', handleEscape);
      }
      return () => document.removeEventListener('keydown', handleEscape);
    }, [open]);

    return (
      <div ref={ref} className={cn('relative', className)} {...props}>
        {/* Trigger button */}
        <button
          ref={buttonRef}
          onClick={() => setOpen(!open)}
          className={cn(
            'flex items-center gap-3 px-2 py-1.5 rounded-xl',
            'border border-transparent transition-all duration-100',
            'hover:bg-grey-100 hover:border-grey-200',
            'dark:hover:bg-grey-800 dark:hover:border-grey-700'
          )}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {/* Avatar */}
          <Avatar className="w-9 h-9 rounded-lg">
            {userPicture ? (
              <AvatarImage src={userPicture} alt={userName} />
            ) : (
              <AvatarFallback className="rounded-lg bg-gradient-to-br from-orange-400 to-red-500 text-white">
                {userInitials}
              </AvatarFallback>
            )}
          </Avatar>

          {/* User info */}
          <div className="flex flex-col items-start min-w-0">
            <span className="text-sm font-semibold text-grey-900 dark:text-grey-50 truncate max-w-[150px]">
              {userName}
            </span>
            <span className="text-xs text-grey-500 dark:text-grey-400">
              {userRole}
            </span>
          </div>

          {/* Chevron */}
          <ChevronDown
            className={cn(
              'w-4 h-4 text-grey-400 transition-transform duration-150',
              open && 'rotate-180'
            )}
          />
        </button>

        {/* Dropdown menu */}
        {open && (
          <div
            ref={menuRef}
            role="menu"
            className={cn(
              'absolute right-0 top-full mt-2 min-w-[240px] z-50',
              'bg-white border border-grey-200 rounded-xl overflow-hidden',
              'shadow-elevation-lg',
              'dark:bg-grey-900 dark:border-grey-800',
              'animate-in fade-in-0 zoom-in-95 duration-150'
            )}
          >
            {/* Header */}
            <div className="px-4 py-3 bg-grey-50 border-b border-grey-200 dark:bg-grey-800 dark:border-grey-700">
              <div className="flex items-center gap-3">
                <Avatar className="w-10 h-10 rounded-lg">
                  {userPicture ? (
                    <AvatarImage src={userPicture} alt={userName} />
                  ) : (
                    <AvatarFallback className="rounded-lg bg-gradient-to-br from-orange-400 to-red-500 text-white">
                      {userInitials}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">
                    {userName}
                  </span>
                  <span className="text-xs text-grey-500 dark:text-grey-400 truncate">
                    {userEmail}
                  </span>
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className="p-2">
              <button
                role="menuitem"
                onClick={() => {
                  onNavigate?.('settings');
                  setOpen(false);
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg',
                  'text-sm font-medium text-grey-700 dark:text-grey-200',
                  'hover:bg-grey-100 dark:hover:bg-grey-800',
                  'transition-colors duration-100'
                )}
              >
                <Settings className="w-[18px] h-[18px] text-grey-500 dark:text-grey-400" />
                Settings
              </button>

              <button
                role="menuitem"
                onClick={() => {
                  onLogout?.();
                  setOpen(false);
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg',
                  'text-sm font-medium text-red-500',
                  'hover:bg-red-50 dark:hover:bg-red-950/30',
                  'transition-colors duration-100'
                )}
              >
                <LogOut className="w-[18px] h-[18px]" />
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
);
UserMenu.displayName = 'UserMenu';
